/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { getLogger } from '../utils/SearchLogger';

// type for flattened stop data
type GradientStopData = {
	offset: number;
	color: RGBColor;
	opacity?: number;
};

// type for returning gradient CSS
type GradientCSS = {
	backgroundImage: string;
	transform: string;
};

/**
 * The superclass for all gradient things
 *
 * @class  {GradientUtils}
 * ? What will happen if the color and opacity stops do not match
 * ? Can there be other types of gradients also
 */

export default class GradientUtils {
	static PRECISION = 100;

	static round(x: number): number {
		return Math.round(x * GradientUtils.PRECISION) / GradientUtils.PRECISION;
	}

	static isGradientData(data: Gradient | any): boolean {
		return data.type && (data.type === 'radialGradient' || data.type === 'linearGradient');
	}

	/**
	 * Covert color data to RGBA
	 * @param  {RGBColor} data
	 * @param  {number=1} opacity
	 * @returns string
	 */
	static colorDataToRGBA(data: RGBColor, opacity = 1): string | null {
		if (!data) {
			return null;
		}

		const red = GradientUtils.round(data.r);
		const green = GradientUtils.round(data.g);
		const blue = GradientUtils.round(data.b);
		const alpha = GradientUtils.round(opacity);
		return `rgba(${red},${green},${blue},${alpha})`;
	}

	/**
	 * Flatten gradient stops
	 * @param  {Array<Stop>} colorStops
	 * @param  {Array<Opacity>} opacityStops
	 * @returns {Array<GradientStopData>} -  Flattened color and opacity stops
	 */
	static flattenGradientStops(colorStops: Array<Stop>, opacityStops: Array<Opacity>): GradientStopData[] {
		// FIXME: Refactor this function if possible
		// * It is assumed that the opacity and color offsets are overlapping
		const stops: Record<number, GradientStopData> = {};
		colorStops.forEach((colorStop) => {
			const roundedOffset = GradientUtils.round(colorStop.offset);
			stops[roundedOffset] = {
				offset: roundedOffset,
				color: colorStop.color,
				opacity: 1,
			};
		});
		opacityStops.forEach((opacityStop) => {
			const roundedOffset = GradientUtils.round(opacityStop.offset);

			stops[roundedOffset].offset = roundedOffset;
			stops[roundedOffset].opacity = GradientUtils.round(opacityStop.opacity);
		});

		// TODO: If the stops don't match interpolate and add fake stops
		const hasColor = (stop: GradientStopData) => stop.color;

		const sortByOffset = (stopA: GradientStopData, stopB: GradientStopData) => stopA.offset - stopB.offset;

		let stopsArray: GradientStopData[] = [];
		stopsArray = stopsArray
			.concat(Object.values(stops) as any)
			.filter(hasColor)
			.sort(sortByOffset);
		return stopsArray;
	}

	/**
	 * Make color stop strings
	 * @param  {Array<Stop>} stops
	 * @param  {Array<Opacity>} opacities
	 * @returns {Array<string>}  -  Array of all the colorStops for the gradient
	 */
	static makeColorStopsStringForGradient(stops: Array<Stop>, opacities: Array<Opacity>): Array<string> {
		const flattenedGradientStops = GradientUtils.flattenGradientStops(stops, opacities);
		const gradient: string[] = [];

		for (let i = 0; i < flattenedGradientStops.length; i++) {
			const stop = flattenedGradientStops[i];
			const rgba = GradientUtils.colorDataToRGBA(stop.color, stop.opacity);
			const offset = stop.offset && `${Math.round(stop.offset * 100)}%`;

			if (rgba) {
				gradient.push(`${rgba} ${offset || ''}`);
			}
		}

		return gradient;
	}

	/**
	 * Gets css for linear gradient rendition
	 * @param  {LinearGradient} data
	 * @returns GradientCSS
	 */
	static getLinearGradientCSS(data: LinearGradient): GradientCSS {
		let {
			// eslint-disable-next-line prefer-const
			value: { stops, opacities, angle },
		} = data;
		angle = angle || 0;
		const gradient: string[] = GradientUtils.makeColorStopsStringForGradient(stops, opacities);
		const transform = '';
		// A Library Gradient's angle is measured anti-clockwise from the positive x-axis (0deg points to the right)
		// A CSS Gradient's angle is measured clockwise from the positive y-axis (0deg points up). So we adjust by 90deg.
		gradient.unshift(`${90 - angle}deg`);
		const backgroundImage = `linear-gradient(${gradient.join(',')})`;
		return {
			backgroundImage,
			transform,
		};
	}

	/**
	 * Gets css for radial gradient rendition
	 * @param  {RadialGradient} data
	 * @returns GradientCSS
	 */
	static getRadialGradientCSS(data: RadialGradient): GradientCSS {
		let {
			// eslint-disable-next-line prefer-const
			value: { stops, opacities, angle, aspectRatio, center },
		} = data;
		angle = angle || 0;
		aspectRatio = aspectRatio || 1;
		const scale = 1;
		// The default centerPoint of a radial gradient is at the center of the container (50% 50%).
		const centerX = center && center.x !== undefined ? center.x : 0.5;
		const centerY = center && center.y !== undefined ? center.y : 0.5;
		const gradient: string[] = GradientUtils.makeColorStopsStringForGradient(stops, opacities);
		// For radial gradients, we will take care of respecting the angle, aspectRatio, scale and center point using the CSS `transform` property
		// Note that while in a CC Gradient, scale refers to the entire width (diameter), in CSS the dimensions are based on the radius, so we need to multiply by .5
		const scaleX = 0.5 * scale;
		const SCALE = Math.ceil(
			2 *
				Math.max(
					Math.max(1, Math.abs(centerX)) / scaleX,
					Math.max(1, Math.abs(centerY)) / (scaleX * aspectRatio),
				),
		);
		// Place a small circle in the center.
		gradient.unshift(`ellipse ${50 / SCALE}% ${50 / SCALE}% at 50% 50%`);
		// Leave default transformOrigin in the center of the container.
		// Scale the radial gradient to respect the scale and aspect ratio, rotate to respect the angle and translate to respect the center point (offset from 0.5 since we render the ellipsis at the center).
		// CCS `rotate` values are measured clockwise from the x-axis
		// while Library Gradient's angle is measured anti-clockwise from the positive x-axis (0deg points right, progresses upward), so flip sign to convert.
		const transform = `translate(${GradientUtils.round((centerX - 0.5) * 100)}%,${GradientUtils.round(
			(centerY - 0.5) * 100,
		)}%) rotate(${-1 * angle}deg) scale(${scale * SCALE},${scale * SCALE * aspectRatio})`;
		const backgroundImage = `radial-gradient(${gradient.join(',')})`;
		return {
			backgroundImage,
			transform,
		};
	}

	/**
	 * Get css for gradient rendition
	 * @param  {RadialGradient|LinearGradient} data
	 * @returns {GradientCSS | null}  - Transform and backgroundImage property for gradient CSS
	 */
	static getGradientCSS(gradientData: Gradient): GradientCSS | null {
		try {
			// If no valid stops return
			if (!gradientData) {
				return null;
			}

			switch (gradientData.type) {
				case 'linearGradient':
					return GradientUtils.getLinearGradientCSS(gradientData as LinearGradient);

				case 'radialGradient':
					return GradientUtils.getRadialGradientCSS(gradientData as RadialGradient);

				default:
					return null;
			}
		} catch (e) {
			getLogger().error('Gradient utils', 'Error failed to create proper css', e.toString());
			return null;
		}
	}
}
